/**
 * Command history table — mirrors the mockup's audit table
 * (`fleet-device-support.html:251-264`: When / Action / By / Reason /
 * Result). Embedded-list rule: capped at 5 rows with a "View all" link to
 * the Phase 4 audit view (not yet built — the link is present, inert until
 * that view ships).
 *
 * Result pills reuse FleetTable's own vocabulary verbatim (no `<Pill>`
 * component in this codebase): `done` emerald, `queued`/`sent` amber
 * (labelled "Queued · device offline" specifically when `sentAt` is null —
 * the UI renders the SERVER'S state, never an optimistic success),
 * `failed`/`expired` rose, `cancelled`/`acked` gray-ish neutral.
 */
import type { FleetActionId, FleetCommand, FleetCommandState } from '@platform/shared-types';
import { FLEET_UI_ACTIONS } from './actionRegistry';

const RESULT_STYLE: Record<FleetCommandState, string> = {
  done: 'bg-emerald-500/15 text-emerald-600',
  queued: 'bg-amber-500/15 text-amber-600',
  sent: 'bg-amber-500/15 text-amber-600',
  acked: 'bg-amber-500/15 text-amber-600',
  failed: 'bg-rose-500/15 text-rose-600',
  expired: 'bg-rose-500/15 text-rose-600',
  cancelled: 'bg-gray-400/20 text-gray-500',
};

const ACTION_LABEL: Partial<Record<FleetActionId, string>> = Object.fromEntries(
  FLEET_UI_ACTIONS.map((a) => [a.id, a.label]),
);
ACTION_LABEL.unlock = 'Unlock till';

function resultLabel(cmd: FleetCommand): string {
  if (cmd.state === 'queued' && !cmd.sentAt) return 'Queued · device offline';
  switch (cmd.state) {
    case 'done': return 'Done';
    case 'queued': return 'Queued';
    case 'sent': return 'Sent';
    case 'acked': return 'Acked';
    case 'failed': return cmd.failureReason ? `Failed · ${cmd.failureReason}` : 'Failed';
    case 'expired': return 'Expired';
    case 'cancelled': return 'Cancelled';
    default: return cmd.state;
  }
}

function whenLabel(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? d.toLocaleTimeString() : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const CAP = 5;

export default function CommandHistory({ commands, onViewAll }: {
  commands: FleetCommand[];
  onViewAll?: () => void;
}) {
  const shown = commands.slice(0, CAP);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-[13px] font-extrabold text-gray-900">Command history</h2>
        <span className="text-[11.5px] text-gray-400">every action, who fired it, what happened</span>
        {commands.length > CAP && (
          <button
            type="button"
            onClick={onViewAll}
            className="ml-auto min-h-[44px] px-2 text-[11.5px] font-bold text-emerald-700 hover:underline"
          >
            View all ({commands.length})
          </button>
        )}
      </div>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="bg-gray-50 text-left text-[10.5px] font-bold uppercase tracking-wide text-gray-400">
            <th className="px-3 py-2">When</th>
            <th className="px-3 py-2">Action</th>
            <th className="px-3 py-2">By</th>
            <th className="px-3 py-2">Reason</th>
            <th className="px-3 py-2">Result</th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No commands sent to this device yet.</td></tr>
          )}
          {shown.map((c) => (
            <tr key={c.commandId} className="border-t border-gray-100 align-top text-gray-600">
              <td className="whitespace-nowrap px-3 py-2 font-mono text-[11.5px] text-gray-500">{whenLabel(c.createdAt)}</td>
              <td className="px-3 py-2 text-gray-900">{ACTION_LABEL[c.action] || c.action}</td>
              <td className="px-3 py-2 font-semibold">{c.requestedBy}</td>
              <td className="px-3 py-2 text-gray-500">{c.reason || '—'}</td>
              <td className="px-3 py-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-extrabold ${RESULT_STYLE[c.state]}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {resultLabel(c)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
